import React, { useEffect, useState } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiTrendingUp } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddCompanyModal from '../components/AddCompanyModal/index';
import CompanyDetailsModal from '../components/CompanyDetailsModal';
import { fetchCompaniesData, fetchCompanyBankId, type Company } from '../service/companiesService';
import Table, { Column } from '../../../components/Table';

export default function Companies() {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [filtered, setFiltered] = useState<Company[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [companyTypes, setCompanyTypes] = useState<string[]>([]);
    const [bankId, setBankId] = useState<string | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const fetchedBankId = await fetchCompanyBankId(uid, isBankUser);
                console.log('Fetched bankId:', fetchedBankId);
                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const companiesData = await fetchCompaniesData(uid, isBankUser);
                    console.log('Companies data received in component:', companiesData);
                    setCompanies(companiesData);
                    setFiltered(companiesData);

                    // Extract unique company types
                    const typesSet = new Set<string>();
                    companiesData.forEach(company => {
                        if (company.companyType) {
                            typesSet.add(company.companyType);
                        }
                    });
                    setCompanyTypes(Array.from(typesSet));
                }
            } catch (err: any) {
                showToast('Failed to load companies data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = companies;

        // Apply filter based on selected company type
        if (selectedFilter && selectedFilter !== 'All') {
            list = list.filter(company => company.companyType === selectedFilter);
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(company => {
                const companyName = (company.companyName || '').toString().toLowerCase();
                const companyId = (company.companyId || '').toString().toLowerCase();
                const companyNo = (company.companyNo || '').toString();
                return companyName.includes(term) || companyId.includes(term) || companyNo.includes(term);
            });
        }

        // Sort by company name alphabetically
        list.sort((a, b) => {
            const nameA = (a.companyName || '').toString();
            const nameB = (b.companyName || '').toString();
            return nameA.localeCompare(nameB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, companies, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(companies);
        setCurrentPage(1);
    };

    const handleStatCardClick = (companyType: string) => {
        setSelectedFilter(selectedFilter === companyType ? null : companyType);
        setCurrentPage(1);
    };

    const openDetailsModal = (company: Company) => {
        setSelectedCompany(company);
        setShowDetailsModal(true);
    };

    const handleCompanyAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const companiesData = await fetchCompaniesData(uid, isBankUser);
            setCompanies(companiesData);

            // Extract unique company types
            const typesSet = new Set<string>();
            companiesData.forEach(c => {
                if (c.companyType) {
                    typesSet.add(c.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            setCurrentPage(1);
            showToast('✅ Company added successfully!', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing companies:', err);
            showToast('❌ Failed to refresh companies', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (company: Company) => {
        if (!bankId || !company.id) return;

        const newStatus = company.status === 'Active' ? 'Inactive' : 'Active';

        setLoading(true);
        try {
            const companyRef = doc(db, 'banks', bankId, 'companies', company.id);
            await updateDoc(companyRef, {
                status: newStatus,
                updatedAt: new Date()
            });

            // Refresh companies list with proper enrichment
            const companiesData = await fetchCompaniesData(uid!, isBankUser);
            setCompanies(companiesData);

            // Extract unique company types
            const typesSet = new Set<string>();
            companiesData.forEach(c => {
                if (c.companyType) {
                    typesSet.add(c.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            showToast(`✅ Company status updated to ${newStatus}!`, 'success');
        } catch (err: any) {
            console.error('Error updating company status:', err);
            showToast('❌ Failed to update company status.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calculate company statistics by type
    const totalCompanies = companies.length;
    const companyTypeCounts = React.useMemo(() => {
        const counts: Record<string, number> = {};
        companies.forEach(company => {
            const type = company.companyType || 'Unknown';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [companies]);

    const columns: Column<Company>[] = [
        {
            header: 'Company Name',
            width: '12%',
            render: (company) => (
                <strong style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{company.companyName}</strong>
            )
        },
        {
            header: 'Type',
            width: '7%',
            render: (company) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {company.companyType || 'N/A'}
                </span>
            )
        },
        {
            header: 'Phone',
            width: '10%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.companyNo || 'N/A'}
                </span>
            )
        },
        {
            header: 'Email',
            width: '12%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.email || 'N/A'}
                </span>
            )
        },
        {
            header: 'Contact Person',
            width: '12%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.contactPersonName || 'N/A'}
                </span>
            )
        },
        {
            header: 'Contact Mobile',
            width: '10%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.contactMobileNumber || 'N/A'}
                </span>
            )
        },
        {
            header: 'Contact Email',
            width: '12%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.contactEmail || 'N/A'}
                </span>
            )
        }
    ];

    if (isBankUser) {
        columns.push({
            header: 'Status',
            width: '6%',
            align: 'center',
            render: (company) => (
                <label className="relative inline-flex cursor-pointer items-center">
                    <input
                        type="checkbox"
                        checked={company.status === 'Active'}
                        onChange={() => handleStatusToggle(company)}
                        className="peer sr-only"
                        disabled={loading}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {company.status || 'Active'}
                    </span>
                </label>
            )
        });
        columns.push({
            header: 'Action',
            width: '6%',
            render: (company) => (
                <button
                    onClick={() => openDetailsModal(company)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    aria-label="Edit company"
                >
                    Edit
                </button>
            )
        });
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading companies...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Cards - Company Types */}
                <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${companyTypes.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-' + Math.min(companyTypes.length + 1, 4)}`}>
                    {/* Total Companies Card */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Vendors</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{totalCompanies}</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="h-full w-full bg-gray-500 dark:bg-gray-500"></div>
                        </div>
                    </div>

                    {/* Company Type Cards */}
                    {companyTypes.map((type) => {
                        const count = companyTypeCounts[type] || 0;
                        const percentage = totalCompanies > 0 ? (count / totalCompanies) * 100 : 0;
                        return (
                            <div
                                key={type}
                                onClick={() => handleStatCardClick(type)}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === type
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{type}</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{percentage.toFixed(1)}% of total</p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id || Math.random().toString()}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No vendors found matching your search' : 'No vendors available'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Vendors"
                    description="Manage and view all vendor information"
                    headerActions={
                        (isBankUser || userRole === 'pso') && (
                            <Button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2"
                            >
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add Vendor
                            </Button>
                        )
                    }
                    filters={
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search by company name or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={selectedFilter || 'All'}
                                    onChange={(e) => setSelectedFilter(e.target.value === 'All' ? null : e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="All">All Types</option>
                                    {companyTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="text-sm whitespace-nowrap"
                                >
                                    Clear Search
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* Toast */}
            {
                toast.show && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toast.message}
                                type={toast.type}
                                onClose={() => setToast({ ...toast, show: false })}
                            />
                        </div>
                    </div>
                )
            }

            {/* Modals */}
            <AddCompanyModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleCompanyAdded}
                bankId={bankId || undefined}
                existingCompanies={companies}
            />

            <CompanyDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                company={selectedCompany}
                bankId={bankId || undefined}
                onUpdate={async () => {
                    if (!uid) return;
                    try {
                        const companiesData = await fetchCompaniesData(uid, isBankUser);
                        setCompanies(companiesData);
                        // Extract unique company types
                        const typesSet = new Set<string>();
                        companiesData.forEach(company => {
                            if (company.companyType) {
                                typesSet.add(company.companyType);
                            }
                        });
                        setCompanyTypes(Array.from(typesSet));
                        showToast('✅ Company updated successfully!', 'success');
                    } catch (err: any) {
                        showToast('❌ Failed to refresh companies', 'error');
                    }
                }}
            />

        </>
    );
}

